import React, { useEffect, useMemo } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import SelectInput from "../shared/inputs/SelectInput";
import useQueryParams from "@/hooks/useQueryParams";
import { useTranslation } from "react-i18next";

/** Sorted unique page numbers + ellipsis markers between gaps. */
export function visiblePageItems(current, total) {
  if (total <= 1) return [1];
  const pages = new Set([1, total]);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("ellipsis");
    out.push(p);
    prev = p;
  }
  return out;
}

const pagePillBase =
  "h-8 min-w-8 shrink-0 rounded-[10px] bg-white border-0 shadow-none text-sm leading-5 text-[#202831] flex items-center justify-center px-2 font-[Inter] outline-none focus-visible:ring-2 focus-visible:ring-[#62769D]/30";

const PaginationComp = ({
  afterId: apiAfterId,
  beforeId: apiBeforeId,
  items = [],
  pageSizeOptions = [10, 20, 30, 40, 50],
  count,
  hasNext = false,
  className,
}) => {
  const { t } = useTranslation();
  const { setParam, getParam, deleteParam, setParams, allParams } =
    useQueryParams();

  const urlPageSize = getParam("pageSize");
  const pageSize = Number(urlPageSize) || 10;

  const urlPage = Number(getParam("page")) || 1;

  const offsetMode =
    typeof count === "number" && !Number.isNaN(count);

  const totalPages = useMemo(() => {
    if (!offsetMode) return 0;
    if (count <= 0) return 1;
    return Math.max(1, Math.ceil(count / pageSize));
  }, [count, pageSize, offsetMode]);

  const displayPage = offsetMode
    ? Math.min(Math.max(1, urlPage), totalPages)
    : urlPage;

  useEffect(() => {
    if (!offsetMode) return;
    if (urlPage > totalPages && totalPages >= 1) {
      setParam("page", String(totalPages));
    }
  }, [offsetMode, urlPage, totalPages, setParam]);

  const clearCursorsAndSetPage = (pageNum) => {
    const next = { ...allParams, page: String(pageNum) };
    delete next.afterId;
    delete next.beforeId;
    setParams(next);
  };

  const handleNext = () => {
    if (offsetMode) {
      if (displayPage >= totalPages) return;
      clearCursorsAndSetPage(displayPage + 1);
      return;
    }
    if (!hasNext) return;
    deleteParam("beforeId");
    setParam("afterId", apiAfterId);
    setParam("page", String(urlPage + 1));
  };

  const handlePrev = () => {
    if (offsetMode) {
      if (displayPage <= 1) return;
      clearCursorsAndSetPage(displayPage - 1);
      return;
    }
    if (urlPage <= 1) return;
    deleteParam("afterId");
    setParam("beforeId", apiBeforeId);
    setParam("page", String(urlPage - 1));
  };

  const handlePageSizeChange = (value) => {
    const next = { ...allParams, pageSize: value, page: "1" };
    delete next.afterId;
    delete next.beforeId;
    setParams(next);
  };

  const pageItems = useMemo(
    () => visiblePageItems(displayPage, totalPages),
    [displayPage, totalPages],
  );

  const showPrev = offsetMode ? displayPage > 1 : urlPage > 1;
  const showNext = offsetMode ? displayPage < totalPages : hasNext;

  const selectTriggerClass =
    "h-8 min-h-8 w-fit rounded-xl border-0 bg-white px-3 py-2 shadow-none ring-0 capitalize font-[Inter] font-semibold text-sm text-[#415466] [&_svg]:text-[#415466] focus:ring-0 focus:ring-offset-0 data-[state=open]:ring-0";

  return (
    <Pagination
      className={cn(
        "flex w-full min-h-8 flex-wrap items-center justify-between gap-6 px-4",
        className ?? "mt-10",
      )}
    >
      <div className="flex h-8 flex-wrap items-center gap-2 font-[Poppins] text-sm leading-5 text-[#909FBA]">
        <span className="whitespace-nowrap">{t("common.pagination.showingPage")}</span>
        <SelectInput
          wrapperClassName="inline-flex w-auto"
          className={selectTriggerClass}
          value={String(pageSize)}
          onChange={handlePageSizeChange}
          options={pageSizeOptions.map((n) => ({
            label: String(n),
            value: String(n),
          }))}
        />
        {offsetMode ? (
          <span className="whitespace-nowrap">
            {t("common.pagination.ofResults", { count })}
          </span>
        ) : (
          <span className="whitespace-nowrap font-[Inter] text-[#415466]">
            {items.length
              ? t("common.pagination.onThisPage", { count: items.length })
              : ""}
          </span>
        )}
      </div>

      <PaginationContent className="flex flex-row flex-wrap items-center gap-1 border-0 bg-transparent p-0">
        {showPrev && (
          <PaginationItem className="border-0 bg-transparent">
            <button
              type="button"
              aria-label={t("common.pagination.ariaPrevious")}
              onClick={handlePrev}
              className={cn(
                pagePillBase,
                "w-8 p-0 text-[#62769D] hover:bg-white/90",
              )}
            >
              <ChevronLeft className="size-[14px]" strokeWidth={2} aria-hidden />
            </button>
          </PaginationItem>
        )}

        {offsetMode &&
          pageItems.map((entry, idx) =>
            entry === "ellipsis" ? (
              <PaginationItem key={`e-${idx}`} className="border-0 bg-transparent">
                <span
                  className={cn(pagePillBase, "cursor-default font-normal")}
                  aria-hidden
                >
                  …
                </span>
              </PaginationItem>
            ) : (
              <PaginationItem key={entry} className="border-0 bg-transparent">
                <button
                  type="button"
                  aria-label={t("common.pagination.ariaPage", { page: entry })}
                  aria-current={entry === displayPage ? "page" : undefined}
                  onClick={() => clearCursorsAndSetPage(entry)}
                  className={cn(
                    pagePillBase,
                    entry === displayPage ? "font-bold" : "font-normal",
                    "hover:bg-white/90",
                  )}
                >
                  {entry}
                </button>
              </PaginationItem>
            ),
          )}

        {showNext && (
          <PaginationItem className="border-0 bg-transparent">
            <button
              type="button"
              aria-label={t("common.pagination.ariaNext")}
              onClick={handleNext}
              className={cn(
                pagePillBase,
                "w-8 p-0 text-[#62769D] hover:bg-white/90",
              )}
            >
              <ChevronRight className="size-[14px]" strokeWidth={2} aria-hidden />
            </button>
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
};

export default PaginationComp;
